/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
import React, {startTransition, useEffect, useMemo, useRef, useState} from 'react'
import PropTypes from 'prop-types'
import {useLocation} from 'react-router-dom'
import {useSearchSuggestions, useShopperContext, useUsid} from '@salesforce/commerce-sdk-react'
import {
    Input,
    InputGroup,
    InputLeftElement,
    Box,
    HStack,
    Image,
    IconButton,
    FormLabel,
    useBreakpointValue,
    useMultiStyleConfig
} from '@salesforce/retail-react-app/app/components/shared/ui'
import SearchSuggestions from './partials/search-suggestions'
import SearchResultsStatus from './partials/search-results-status'
import {
    capitalize,
    boldString,
    getSessionJSONItem,
    setSessionJSONItem,
    isServer
} from '@salesforce/retail-react-app/app/utils/utils'
import useNavigation from '@salesforce/retail-react-app/app/hooks/use-navigation'
import debounce from 'lodash/debounce'
import {
    RECENT_SEARCH_KEY,
    RECENT_SEARCH_LIMIT,
    RECENT_SEARCH_MIN_LENGTH
} from '@salesforce/retail-react-app/app/constants'
import LoadingSpinner from '../loading-spinner'
import useTagManager from '../../hooks/use-tag-manager'
import {useIntl} from 'react-intl'
import {searchUrlBuilder} from '../../utils/url'
import {useSiteContext} from '../../contexts/site'
import {SearchIcon} from '../icons'
import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config'
import useBloomreach from '../../hooks/use-bloomreach'
import {useGlobaleContext} from '../../contexts/globale'
import {getUrl} from '../../utils/cloudinary-utils'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'
import {scheduleAfterPaint, scheduleIdleAnalytics} from '../../utils/common-utils'

const formatBloomreachSuggestions = (
    {attributeSuggestions, querySuggestions, searchSuggestions},
    input
) => {
    return {
        categorySuggestions: attributeSuggestions?.map((suggestion) => {
            return {
                type: 'category',
                id: suggestion.value,
                link: suggestion.value,
                isBloomreach: true,
                name: boldString(suggestion.name, capitalize(input))
            }
        }),
        productSuggestions: searchSuggestions?.map((product) => {
            const {sku_price, sku_thumb_images} =
                product?.variants?.find((v) => v?.variant_url?.includes(product?.variant_url)) || {}
            return {
                type: 'product',
                currency: 'USD', // Hardcode Currency
                price: sku_price || product.sale_price,
                productId: product.pid,
                disBaseLink: sku_thumb_images?.[0] || product?.thumb_image,
                name: boldString(product.title, capitalize(input)),
                link: product?.variant_url || product?.url,
                c_globalePrice: sku_price || product.sale_price,
                c_selectedCurrency: 'USD', // Hardcode Currency
                c_selectedCurrencySymbol: '$' // Hardcode Currency
            }
        }),
        phraseSuggestion: querySuggestions?.map((product) => {
            return {
                displayText: product.query,
                query: product.displayText,
                isPhrase: true
            }
        })
    }
}

const formatSfccSuggestions = (searchSuggestions, input) => {
    return {
        categorySuggestions: searchSuggestions?.categorySuggestions?.categories?.map(
            (suggestion) => {
                return {
                    type: 'category',
                    id: suggestion.id,
                    link: suggestion.c_link,
                    isExternalRedirection: suggestion.c_isExternalRedirection,
                    name: boldString(suggestion.name, capitalize(input))
                }
            }
        ),
        productSuggestions: searchSuggestions?.productSuggestions?.products?.map((product) => {
            return {
                type: 'product',
                currency: product.currency,
                price: product.price,
                productId: product.productId,
                disBaseLink: product?.image?.disBaseLink,
                name: boldString(product.productName, capitalize(input)),
                link: product?.c_link,
                c_globalePrice: product?.c_globalePrice,
                c_selectedCurrency: product?.c_selectedCurrency,
                c_selectedCurrencySymbol: product?.c_selectedCurrencySymbol
            }
        }),
        phraseSuggestion:
            searchSuggestions?.productSuggestions?.suggestedTerms?.[0]?.terms?.[0]?.value
    }
}

/**
 * The SearchInput component is a stylized
 * text input made specifically for use in
 * the application header.
 * @param  {object} props
 * @param  {object} ref reference to the input element
 * @return  {React.ReactElement} - SearchInput component
 */
const Search = (props) => {
    const styles = useMultiStyleConfig('Header')
    const isDesktop = useBreakpointValue({base: false, lg: true})
    const [searchQuery, setSearchQuery] = useState('')
    const navigate = useNavigation()
    const tagManager = useTagManager()
    const {site} = useMultiSite()
    const {usid} = useUsid()
    const location = useLocation()
    const {isYR} = useSiteContext()
    const intl = useIntl()
    const {
        app: {commerceAPI}
    } = getConfig()
    const {bloomreach} = commerceAPI
    const {config} = bloomreach || {}
    var enableBloomreachSuggestions = false
    try {
        enableBloomreachSuggestions = JSON.parse(config).enableBloomreachSuggestions
    } catch (e) {
        console.log(e.message)
    }
    const [recentSearches, setRecentSearches] = useState([])
    const {countryISO, currencyCode} = useGlobaleContext()

    const searchSuggestion = useSearchSuggestions(
        {
            parameters: {
                q: searchQuery,
                c_selectedCountry: countryISO,
                c_selectedCurrency: currencyCode
            }
        },
        {
            enabled: searchQuery?.length >= RECENT_SEARCH_MIN_LENGTH && !enableBloomreachSuggestions
        }
    )

    const searchInputRef = useRef()
    const brSearchSuggestion = useBloomreach(
        searchQuery,
        searchQuery &&
            searchQuery?.length >= RECENT_SEARCH_MIN_LENGTH &&
            enableBloomreachSuggestions
    )
    const searchSuggestions = useMemo(
        () =>
            enableBloomreachSuggestions
                ? formatBloomreachSuggestions(
                      brSearchSuggestion?.data?.suggestionGroups?.[0] || {},
                      searchInputRef?.current?.value
                  )
                : formatSfccSuggestions(searchSuggestion.data, searchInputRef?.current?.value),
        [enableBloomreachSuggestions, brSearchSuggestion?.data, searchSuggestion.data, searchQuery]
    )
    const activeSuggestion = enableBloomreachSuggestions ? brSearchSuggestion : searchSuggestion
    const isSearchOpen = Boolean(
        props.hideCategories || props.showSearchTab || props.showCloseDrawer === false
    )

    // Fetch shopper context if Bloomreach search is enabled and USID is available
    const {data: shopperContext} = useShopperContext(
        {
            parameters: {
                usid: usid || (!isServer && localStorage.getItem('pdUsid')),
                siteId: site?.id
            }
        },
        {
            enabled:
                !isServer &&
                enableBloomreachSuggestions &&
                (Boolean(usid) || Boolean(localStorage.getItem('pdUsid')))
        }
    )

    /** Format the first 5 products returned by Bloomreach **/
    const formatProduct = (product) => {
        const match = product?.variants?.find((v) => v?.variant_url?.includes(product?.variant_url))
        const {sku_price, sku_thumb_images} = match || {}
        return {
            type: 'product',
            currency: 'USD', // Hardcode currency
            price: sku_price || product.sale_price,
            productId: product.pid,
            disBaseLink: sku_thumb_images?.[0] || product?.thumb_image,
            name: product.title,
            link: product?.variant_url || product?.url,
            c_globalePrice: sku_price || product.sale_price,
            c_selectedCurrency: 'USD', // Hardcode currency
            c_selectedCurrencySymbol: '$' // Hardcode currency
        }
    }

    /** Fetch phrase product previews for the desktop hover state only. **/
    const suggestion1 = useBloomreach(
        searchSuggestions?.phraseSuggestion?.[0]?.query,
        isDesktop,
        true
    )
    const suggestion2 = useBloomreach(
        searchSuggestions?.phraseSuggestion?.[1]?.query,
        isDesktop,
        true
    )
    const suggestion3 = useBloomreach(
        searchSuggestions?.phraseSuggestion?.[2]?.query,
        isDesktop,
        true
    )
    const suggestion4 = useBloomreach(
        searchSuggestions?.phraseSuggestion?.[3]?.query,
        isDesktop,
        true
    )
    const suggestion5 = useBloomreach(
        searchSuggestions?.phraseSuggestion?.[4]?.query,
        isDesktop,
        true
    )

    /** Fetch category product previews for the desktop hover state only. **/
    const category1 = useBloomreach(
        searchSuggestions?.categorySuggestions?.[0]?.id,
        isDesktop,
        true,
        true,
        shopperContext?.effectiveDateTime
    )
    const category2 = useBloomreach(
        searchSuggestions?.categorySuggestions?.[1]?.id,
        isDesktop,
        true,
        true,
        shopperContext?.effectiveDateTime
    )
    const category3 = useBloomreach(
        searchSuggestions?.categorySuggestions?.[2]?.id,
        isDesktop,
        true,
        true,
        shopperContext?.effectiveDateTime
    )
    const category4 = useBloomreach(
        searchSuggestions?.categorySuggestions?.[3]?.id,
        isDesktop,
        true,
        true,
        shopperContext?.effectiveDateTime
    )
    const category5 = useBloomreach(
        searchSuggestions?.categorySuggestions?.[4]?.id,
        isDesktop,
        true,
        true,
        shopperContext?.effectiveDateTime
    )

    /** Save response of the first 5 query specific product suggestions. **/
    const phraseResponses = {
        [searchSuggestions?.phraseSuggestion?.[0]?.query]: suggestion1?.data?.map((p) =>
            formatProduct(p)
        ),
        [searchSuggestions?.phraseSuggestion?.[1]?.query]: suggestion2?.data?.map((p) =>
            formatProduct(p)
        ),
        [searchSuggestions?.phraseSuggestion?.[2]?.query]: suggestion3?.data?.map((p) =>
            formatProduct(p)
        ),
        [searchSuggestions?.phraseSuggestion?.[3]?.query]: suggestion4?.data?.map((p) =>
            formatProduct(p)
        ),
        [searchSuggestions?.phraseSuggestion?.[4]?.query]: suggestion5?.data?.map((p) =>
            formatProduct(p)
        )
    }

    /** Save response of the first 5 category specific product suggestions. **/
    const categoryResponses = {}

    const categories = [category1, category2, category3, category4, category5]

    searchSuggestions?.categorySuggestions?.slice(0, 5)?.forEach((cat, i) => {
        const data = categories[i]?.data
        if (data) {
            categoryResponses[cat.id] = data.map((p) => formatProduct(p))
        }
    })

    const saveRecentSearch = (searchText) => {
        // Get recent searches or an empty array if undefined.
        let searches = getSessionJSONItem(RECENT_SEARCH_KEY) || []

        // Check if term is already in the saved searches
        searches = searches.filter((savedSearchTerm) => {
            return searchText.toLowerCase() !== savedSearchTerm.toLowerCase()
        })

        // Create a new array consisting of the search text and up to 4 other resent searches.
        // I'm assuming the order is newest to oldest.
        searches = [searchText, ...searches].slice(0, RECENT_SEARCH_LIMIT)

        // Replace the save resent search with the updated value.
        setSessionJSONItem(RECENT_SEARCH_KEY, searches)
    }

    // Keep one debounced function for the lifetime of the search input. Recreating it on
    // render leaves older timers alive, which can trigger stale suggestion requests while
    // the user is still typing.
    const debouncedSearch = useMemo(
        () =>
            debounce((input) => {
                startTransition(() => setSearchQuery(input))
            }, 300),
        []
    )

    useEffect(() => () => debouncedSearch.cancel(), [debouncedSearch])

    const onSearchChange = (e) => {
        const input = e.target.value
        if (input.length >= RECENT_SEARCH_MIN_LENGTH) {
            debouncedSearch(input)
        } else {
            debouncedSearch.cancel()
            startTransition(() => setSearchQuery(''))
        }
    }

    const clearInput = () => {
        debouncedSearch.cancel()
        if (searchInputRef.current) {
            searchInputRef.current.value = ''
            searchInputRef.current.blur()
        }
        startTransition(() => setSearchQuery(''))
    }

    const closeSearch = () => {
        clearInput()
        startTransition(() => {
            props.setShowSearchTab?.(false)
            props.setHideCategories?.(false)
            props.setShowBasket?.(true)
            props.setShowCloseDrawer?.(true)
        })
    }

    useEffect(() => {
        if (!props.closeSearchRef) return undefined
        props.closeSearchRef.current = closeSearch
        return () => {
            props.closeSearchRef.current = null
        }
    }, [props.closeSearchRef, closeSearch])

    const onSubmitSearch = (e) => {
        e.preventDefault()
        // Avoid blank spaces to be searched
        let searchText = searchInputRef.current.value.trim()
        // Avoid empty string searches
        if (searchText.length < 1) {
            return
        }
        closeSearch()
        window.localStorage.setItem('product_search_term', searchText)
        saveRecentSearch(searchText)
        scheduleIdleAnalytics(() => {
            tagManager.siteSearch(
                searchText,
                searchSuggestions?.productSuggestions?.length?.toString() || '0',
                undefined,
                'typed-term'
            )
        })
        scheduleAfterPaint(() => {
            startTransition(() => {
                navigate(searchUrlBuilder(isYR, searchText))
            })
        })
    }

    const closeAndNavigate = (link, saveSearch, externalRedirection) => {
        if (saveSearch) {
            // If user clicks the suggested phrase, then save it in recent searches
            // Avoid blank spaces to be searched
            let searchText = searchInputRef.current.value.trim()
            // Avoid empty string searches
            if (searchText.length < 1) {
                return
            }
            scheduleIdleAnalytics(() => {
                saveRecentSearch(searchText)
            })
        }

        if (!link) {
            closeSearch()
            return
        }

        if (externalRedirection && props.setShowSpinner) {
            props.setShowSpinner(true)
        }

        scheduleAfterPaint(() => {
            startTransition(() => {
                closeSearch()
                if (!externalRedirection) {
                    navigate(link)
                }
            })

            if (externalRedirection) {
                // Keep cross-brand navigation as a full page load.
                window.location.href = link
            }
        })
    }

    useEffect(() => {
        // If user change the brand in composable site, we need to close the search modal
        setHideCategories(false)
        setShowCloseDrawer(true)
    }, [location?.pathname])

    const onSearchInputChange = (e) => {
        onSearchChange(e)
    }

    const setHideCategories = (bool) => {
        if (props.setHideCategories) {
            props.setHideCategories(bool)
        }
    }

    const setShowCloseDrawer = (bool) => {
        if (props.setShowCloseDrawer) {
            props.setShowCloseDrawer(bool)
        }
    }

    const revealSearch = () => {
        startTransition(() => {
            setHideCategories(true)
            setShowCloseDrawer(false)
        })
    }

    const openSearch = () => {
        revealSearch()
        scheduleIdleAnalytics(() => {
            tagManager.formFieldChange('simpleSearch', 'Form Submit Intent')
        })
    }

    useEffect(() => {
        setRecentSearches(getSessionJSONItem(RECENT_SEARCH_KEY) || [])
        if (props.setShowBasket) {
            props.setShowBasket(!searchQuery)
        }
    }, [searchQuery, props.setShowBasket, props.setShowCloseDrawer, props?.isMenu])

    useEffect(() => {
        // Note: Not a reactJS practice, we need to close the search modal if user clicks the area below the search modal.
        const handleDocumentClick = (event) => {
            if (event.target.closest('#app-main') && !event.target.closest('.try-new-search')) {
                closeAndNavigate()
            }
        }

        document.addEventListener('click', handleDocumentClick)
        return () => document.removeEventListener('click', handleDocumentClick)
    }, [])
    return (
        <Box className="search-bar-box">
            <SearchResultsStatus
                inputRef={searchInputRef}
                query={searchQuery}
                suggestions={searchSuggestions}
                ready={Boolean(
                    isSearchOpen &&
                        searchQuery &&
                        activeSuggestion?.data &&
                        !activeSuggestion.isFetching &&
                        !activeSuggestion.isPreviousData &&
                        !activeSuggestion.isError
                )}
            />
            {searchSuggestion?.isLoading && searchSuggestion?.isFetching && <LoadingSpinner />}
            <Box
                className="search-bar"
                width={{base: props.isSearchDrawer ? '100%' : '97%', lg: 'unset'}}
                data-is-menu={props.isMenu}
            >
                <form onSubmit={onSubmitSearch} className="try-new-search">
                    <HStack className="search-stack">
                        <InputGroup
                            {...styles.inputGroup}
                            sx={{
                                '& input::placeholder': {
                                    color: {
                                        base: isYR && props?.isMenu ? 'white' : 'gray.800',
                                        lg: 'gray.800'
                                    },
                                    fontWeight: '400',
                                    fontSize: '14px !important'
                                }
                            }}
                            className="search-input-group"
                        >
                            <InputLeftElement
                                height="36px"
                                cursor="pointer"
                                onClick={(e) => {
                                    let searchText = searchInputRef.current.value.trim()
                                    // Avoid empty string searches
                                    if (searchText.length < 1) {
                                        openSearch()
                                        return
                                    }
                                    onSubmitSearch(e)
                                }}
                                className="input-left-element"
                                left={{base: 0, lg: props.showSearchTab ? 'unset' : '8px', xl: '0'}}
                            >
                                <SearchIcon
                                    alt="Search Logo"
                                    width="15px"
                                    height="15px"
                                    verticalAlign="middle"
                                    color={{
                                        base: isYR && props?.isMenu ? 'white' : 'black',
                                        lg: 'black'
                                    }}
                                />
                            </InputLeftElement>
                            <FormLabel
                                htmlFor={'search-input-' + props?.id}
                                display="none"
                                className="input-form-label"
                            >
                                {intl.formatMessage({
                                    id: 'header.button.assistive_msg.search',
                                    defaultMessage: 'Search'
                                })}
                            </FormLabel>
                            <Input
                                data-brand={isYR ? 'yr' : 'ks'}
                                width={{
                                    base: '100%',
                                    md: '100%',
                                    lg: props.hideCategories ? '80vw' : '55px',
                                    xl: props.hideCategories ? '76vw' : '270px'
                                }}
                                id={'search-input-' + props?.id}
                                aria-label={intl.formatMessage({
                                    id: 'header.button.assistive_msg.search',
                                    defaultMessage: 'Search'
                                })}
                                borderRadius={{
                                    lg: props.hideCategories ? '10px' : '50px',
                                    xl: '10px',
                                    base: '10px'
                                }}
                                transition={{
                                    base: !props.hideCategories ? 'none' : 'width 0.3s ease'
                                }}
                                {...styles.input}
                                className="open-search"
                                onClick={openSearch}
                                onFocus={revealSearch}
                                color={{
                                    base: isYR && props?.isMenu ? 'white' : 'black.300',
                                    lg: 'black.300'
                                }}
                                backgroundColor={{
                                    base: isYR && props?.isMenu ? 'whiteAlpha25' : 'gray.50',
                                    lg: 'gray.50'
                                }}
                                placeholder={props.placeholder}
                                onChange={(e) => onSearchInputChange(e)}
                                ref={searchInputRef}
                            />
                        </InputGroup>
                    </HStack>
                </form>
            </Box>
            <Box
                display={
                    props.isMenu
                        ? 'none'
                        : {
                              base: 'block',
                              lg: searchQuery || props.hideCategories ? 'inline-block' : 'none'
                          }
                }
                {...styles.inputWrapper}
                {...(props.isSearchDrawer ? {right: '15px'} : {})}
                top={{base: props?.isMenu ? '12px' : '18px', lg: '10px', xl: '10px'}}
            >
                <IconButton
                    onClick={closeSearch}
                    aria-label={intl.formatMessage({
                        id: 'header.button.assistive_msg.close_search',
                        defaultMessage: 'Close search'
                    })}
                    zIndex="1"
                    {...styles.iconButton}
                    icon={
                        <Image
                            src={getUrl('UIImages/images/black-cross-icon.svg')}
                            height="16px"
                            width="16px"
                            verticalAlign="middle"
                            alt="Close Icon"
                        />
                    }
                />
            </Box>
            <Box
                className="search-suggestions"
                {...styles.suggestions}
                bg={{lg: 'white'}}
                alignItems="center"
                position="absolute"
                margin={{base: props.isSearchDrawer ? '0 15px 200px' : '0 0 200px 15px', lg: '0'}}
                overflowY={{base: 'auto', lg: 'unset'}}
                overflowX={{base: 'clip', lg: 'unset'}}
                maxH={{base: props?.isMenu ? '100vh' : '100%', lg: 'none'}}
                width={{
                    base: props.isSearchDrawer
                        ? 'calc(100% - 30px)'
                        : props?.isMenu
                        ? 'calc(100% - 38px)'
                        : 'unset',
                    lg: '100vw'
                }}
            >
                {(props.hideCategories ||
                    props.showSearchTab ||
                    props.showCloseDrawer === false) && (
                    <SearchSuggestions
                        searchQuery={searchQuery}
                        phraseResponses={phraseResponses}
                        categoryResponses={categoryResponses}
                        searchSuggestions={searchSuggestions}
                        enableBloomreachSuggestions={enableBloomreachSuggestions}
                        closeAndNavigate={closeAndNavigate}
                        recentSearches={recentSearches}
                    />
                )}
            </Box>
        </Box>
    )
}

Search.propTypes = {
    isMenu: PropTypes.bool,
    isSearchDrawer: PropTypes.bool,
    closeSearchRef: PropTypes.shape({current: PropTypes.func}),
    showSearchTab: PropTypes.bool,
    setShowSearchTab: PropTypes.func,
    hideCategories: PropTypes.bool,
    setHideCategories: PropTypes.func,
    setShowBasket: PropTypes.func,
    isStickyBackSection: PropTypes.bool,
    setShowCloseDrawer: PropTypes.func,
    setShowSpinner: PropTypes.func,
    showCloseDrawer: PropTypes.bool,
    placeholder: PropTypes.any,
    ariaLabel: PropTypes.string,
    id: PropTypes.string
}

Search.displayName = 'SearchInput'

export default Search
